import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { ArrowUpCircle, RefreshCw } from "lucide-react";

const CHECK_INTERVAL_MS = 60_000;

function extractBuildIdFromHtml(html: string): string | null {
  const match = html.match(/<script id="__NEXT_DATA__" type="application\/json">([\s\S]*?)<\/script>/i);
  if (!match?.[1]) return null;
  try {
    const data = JSON.parse(match[1]) as { buildId?: string };
    return data.buildId ?? null;
  } catch {
    return null;
  }
}

function findCurrentBuildId(): string | null {
  const globalData = (window as Window & { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__;
  return globalData?.buildId ?? null;
}

export function VersionRefreshPrompt() {
  const promptedRef = useRef(false);
  const currentBuildIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    currentBuildIdRef.current = findCurrentBuildId();
    if (!currentBuildIdRef.current) return;

    let timer: number | null = null;
    let stopped = false;

    const checkForUpdate = async () => {
      if (stopped || promptedRef.current) return;
      try {
        const res = await fetch(window.location.pathname || "/", {
          cache: "no-store",
          headers: { "cache-control": "no-cache" },
        });
        if (!res.ok) return;
        const html = await res.text();
        const latestBuildId = extractBuildIdFromHtml(html);
        if (!latestBuildId || latestBuildId === currentBuildIdRef.current) return;

        promptedRef.current = true;
        toast.custom(
          (t) => (
            <div className="w-[calc(100vw-1.5rem)] max-w-[560px] overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-background via-background to-primary/5 shadow-2xl">
              <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
                  <ArrowUpCircle className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold tracking-tight text-foreground sm:text-base">
                    Update ready
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    A new version is available. Refresh to load the latest changes.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    toast.dismiss(t);
                    window.location.reload();
                  }}
                  className="inline-flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 sm:w-auto"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
              </div>
            </div>
          ),
          {
            duration: Infinity,
            position: "bottom-center",
            className: "!bg-transparent !border-0 !shadow-none !p-0 overflow-visible",
          },
        );
      } catch {
        // network/cache hiccup: ignore and retry later
      }
    };

    void checkForUpdate();
    timer = window.setInterval(() => {
      void checkForUpdate();
    }, CHECK_INTERVAL_MS);

    return () => {
      stopped = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, []);

  return null;
}
